import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Chip,
} from '@mui/material';

const HomePage = () => {
  const navigate = useNavigate();
  const ciphers = [
    {
      id: 'aristocrat',
      name: 'Aristocrat Cipher',
      difficulty: 'medium',
      description: 'Monoalphabetic substitution cipher that preserves word boundaries and spacing',
      example: 'A → D',
      tip: 'Look for common patterns and short words. Single letters are usually "A" or "I".',
    },
    {
      id: 'atbash',
      name: 'Atbash Cipher',
      difficulty: 'easy',
      description: 'Each letter is mapped to its reverse counterpart in the alphabet (A → Z, B → Y, etc.)',
      example: 'A → Z',
      tip: 'Write out the alphabet and its reverse to help you decode the message.',
    },
    {
      id: 'baconian',
      name: 'Baconian Cipher',
      difficulty: 'medium',
      description: 'Each letter is encoded as a 5-character binary pattern using various obfuscation schemes',
      example: 'A → AAAAA',
      tip: 'Look for patterns in the encoding scheme (capitals/lowercase, italics/regular, or consonants/vowels).',
    },
    {
      id: 'caesar',
      name: 'Caesar Cipher',
      difficulty: 'easy',
      description: 'Classic shift cipher where each letter is moved a fixed number of positions in the alphabet',
      example: 'A → D (shift: 3)',
      tip: 'Each letter is shifted by the same number of positions in the alphabet. Try different shifts from 1-25.',
    },
    {
      id: 'vigenere',
      name: 'Vigenère Cipher',
      difficulty: 'medium',
      description: 'A polyalphabetic cipher that uses a keyword to shift letters in a repeating pattern',
      example: 'ATTACK AT DAWN\nKey: LEMON\nResult: LXFOPV EF RNHR',
      tip: 'Try to find the keyword length by looking for repeated patterns in the ciphertext.',
    },
    {
      id: 'hill',
      name: 'Hill Cipher',
      difficulty: 'hard',
      description: 'A polyalphabetic cipher that uses matrix multiplication to transform blocks of letters',
      example: 'HELLO + Key Matrix:\n[2, 3]\n[1, 4]\n→ HKRSU',
      tip: 'Uses matrix multiplication to encode blocks of letters. The key matrix must be invertible modulo 26.',
    },
  ];

  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'easy':
        return 'success';
      case 'medium':
        return 'warning';
      case 'hard':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#ffffff',
        py: 6,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative background shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          opacity: 0.1,
          background: `
            radial-gradient(circle at 10% 10%, rgba(0,0,0,0.1) 0%, transparent 20%),
            radial-gradient(circle at 90% 90%, rgba(0,0,0,0.1) 0%, transparent 20%),
            radial-gradient(circle at 90% 10%, rgba(0,0,0,0.1) 0%, transparent 20%),
            radial-gradient(circle at 10% 90%, rgba(0,0,0,0.1) 0%, transparent 20%)
          `,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h1" 
            component="h1" 
            gutterBottom 
            sx={{ 
              color: '#000000',
              fontWeight: 700,
              textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
              mb: 4,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100px',
                height: '3px',
                background: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%)',
                borderRadius: '2px',
              }
            }}
          >
            Science Olympiad Codebusters
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 4, 
              color: 'rgba(0,0,0,0.8)'
            }}
          >
            Practice decoding various ciphers interactively. Choose a cipher below to begin your challenge!
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {ciphers.map((cipher) => (
            <Grid item xs={12} sm={6} md={4} key={cipher.id}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.2s ease-in-out',
                  backgroundColor: '#ffffff',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Typography 
                  variant="h6" 
                  component="h2" 
                  gutterBottom
                  sx={{
                    position: 'relative',
                    pb: 2,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '60px',
                      height: '2px',
                      background: 'linear-gradient(90deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 100%)',
                      borderRadius: '2px',
                    }
                  }}
                >
                  {cipher.name}
                </Typography>

                <Chip
                  label={cipher.difficulty}
                  color={getDifficultyColor(cipher.difficulty)}
                  size="small"
                  sx={{ alignSelf: 'flex-start', mb: 2 }}
                />

                <Typography variant="body2" sx={{ mb: 2 }}>
                  {cipher.description}
                </Typography>

                <Box sx={{ 
                  mb: 2,
                  p: 2,
                  bgcolor: 'rgba(0,0,0,0.04)',
                  borderRadius: 1,
                  fontFamily: 'monospace'
                }}>
                  <Typography variant="caption" display="block" gutterBottom>
                    Example:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {cipher.example}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, fontSize: '0.875rem' }}
                >
                  Tip: {cipher.tip}
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate(`/${cipher.id}`)}
                  sx={{ mt: 'auto' }}
                >
                  Start Challenge
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default HomePage; 